/**
 * Partner connect via Atlas (EPD-11850).
 *
 * Lovable's connect link goes through Atlas, which logs the start and then
 * sends the creator to `/connect?source=lovable&return=<Atlas return URL>`.
 * The Atlas return URL is `https://<atlas host>/api/partner-connect/return?state=<opaque>`.
 * On the way back the Portal appends the connected accounts, signed by the
 * API (see apps/api/lib/partner-connect-return.ts), so Atlas can record them.
 *
 * Shared by the app (is this return target Atlas?) and the API (only sign
 * URLs that really are Atlas's return endpoint).
 */

/** The only path an Atlas return URL may have. */
export const ATLAS_PARTNER_CONNECT_RETURN_PATH = "/api/partner-connect/return";

/** Most accounts sent back to Atlas in one return. */
export const MAX_PARTNER_CONNECT_ACCOUNTS = 10;

/** Longest Atlas return URL accepted. */
export const MAX_ATLAS_RETURN_URL_LENGTH = 2000;

export const PARTNER_CONNECT_NETWORKS = [
  "instagram",
  "tiktok",
  "youtube",
] as const;

export type PartnerConnectNetwork = (typeof PARTNER_CONNECT_NETWORKS)[number];

/** One connected account, exactly as the Atlas contract names the fields. */
export interface PartnerConnectAccount {
  network: PartnerConnectNetwork;
  /** IG user id / TikTok open_id / YouTube channel id. */
  id: string;
  username: string | null;
}

export function isPartnerConnectNetwork(
  value: unknown,
): value is PartnerConnectNetwork {
  return (
    typeof value === "string" &&
    (PARTNER_CONNECT_NETWORKS as readonly string[]).includes(value)
  );
}

// Hosts where a plain-http Atlas (local dev on localhost:10000) is accepted
const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);

/**
 * Parses a comma-separated Atlas host list (`host` or `host:port`, e.g.
 * `atlas.aspire.io,alpha.aspire.io`) into lowercase entries.
 */
export function parseAtlasHosts(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((host) => host.trim().toLowerCase())
    .filter((host) => host.length > 0);
}

/**
 * True when `value` is an Atlas partner-connect return URL: https (plain http
 * only for a loopback host), a host from `atlasHosts` matched exactly
 * (including any port), no credentials, and exactly the return path.
 */
export function isAtlasReturnUrl(
  value: string | undefined | null,
  atlasHosts: readonly string[],
): boolean {
  if (!value || value.length > MAX_ATLAS_RETURN_URL_LENGTH) return false;
  if (atlasHosts.length === 0) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  const isLoopback = LOOPBACK_HOSTNAMES.has(url.hostname);
  if (url.protocol !== "https:" && !(isLoopback && url.protocol === "http:"))
    return false;
  if (url.username || url.password) return false;
  if (!atlasHosts.includes(url.host.toLowerCase())) return false;
  return url.pathname === ATLAS_PARTNER_CONNECT_RETURN_PATH;
}
